const MS_IN_MINUTE = 60 * 1000;
const MS_IN_HOUR = 60 * MS_IN_MINUTE;
const DAILY_MINIMUM_RENTAL_MS = 60 * MS_IN_MINUTE;

const monthNamesShortEn = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];

const pad2 = (value) => String(value).padStart(2, "0");

const cloneDate = (value) => new Date(value.getTime());

const parseDateLike = (value) => {
    if (!value) return null;
    const parsed = value instanceof Date ? new Date(value.getTime()) : new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    parsed.setSeconds(0, 0);
    return parsed;
};

const roundedDateWithInterval = (date, interval) => {
    const safeStep = Math.max(1, Math.round(interval));
    const rounded = new Date(date);
    rounded.setSeconds(0, 0);
    rounded.setMinutes(Math.floor(rounded.getMinutes() / safeStep) * safeStep);
    return rounded;
};

const calculateDayExtensionHours = (startDate, endDate) => {
    const reference = endDate ? new Date(endDate) : new Date(startDate);
    const nextDay = new Date(reference);
    nextDay.setDate(nextDay.getDate() + 1);
    const diffHours = Math.round((nextDay.getTime() - reference.getTime()) / MS_IN_HOUR);
    if (diffHours <= 23) return 23;
    return 24;
};

const formatDate = (date) => `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`;

const formatTime = (date) => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

const formatDateTime = (date) => `${formatDate(date)} ${formatTime(date)}`;

const formatPopupDateTime = (date) => `${pad2(date.getDate())} ${monthNamesShortEn[date.getMonth()]} ${formatTime(date)}`;

const formatPopupDuration = (durationMs) => {
    const safeMs = Math.max(0, durationMs);
    const totalMinutes = Math.floor(safeMs / MS_IN_MINUTE);
    const totalHours = Math.floor(totalMinutes / 60);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = totalMinutes % 60;
    const dayLabel = days === 1 ? "Day" : "Days";
    const hourLabel = hours === 1 ? "Hour" : "Hours";
    const minuteLabel = minutes === 1 ? "Minute" : "Minutes";

    if (days > 0) {
        return `${days} ${dayLabel}, ${hours} ${hourLabel}, ${minutes} ${minuteLabel}`;
    }
    return `${hours} ${hourLabel}, ${minutes} ${minuteLabel}`;
};

const toInputDateValue = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const getMinuteOptions = (step) => {
    const safeStep = Math.max(1, Math.round(step));
    const minuteStep = safeStep >= 60 ? 60 : safeStep;
    const options = [];

    for (let minute = 0; minute < 60; minute += minuteStep) {
        options.push(minute);
    }

    if (!options.length) return [0];
    return options;
};

const alignMinuteToStep = (minute, step) => {
    const safeStep = Math.max(1, Math.round(step));
    const minuteStep = safeStep >= 60 ? 60 : safeStep;
    return Math.floor(minute / minuteStep) * minuteStep;
};

const setDatePortion = (baseDate, inputDateValue) => {
    const [year, month, day] = String(inputDateValue || "").split("-").map((value) => Number(value));
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        return baseDate;
    }
    const next = cloneDate(baseDate);
    next.setFullYear(year, month - 1, day);
    return next;
};

const setTimePortion = (baseDate, hour, minute) => {
    const next = cloneDate(baseDate);
    next.setHours(hour, minute, 0, 0);
    return next;
};

const isSameCalendarDate = (left, right) => (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
);

const getScheduleEntryForDate = (date, schedule) => {
    if (!Array.isArray(schedule) || !date) return null;
    const dayIndex = date.getDay() === 0 ? 7 : date.getDay();
    return schedule.find((entry) => Number(entry?.dayOfWeek) === dayIndex) || null;
};

const getOpeningMinutesByDate = (date, schedule) => {
    const entry = getScheduleEntryForDate(date, schedule);
    if (!entry || entry.isClosed) return null;
    if (entry.isOpen24h) return 0;
    const openHour = Number(entry.openHour);
    const openMinute = Number(entry.openMinute);
    if (!Number.isFinite(openHour) || !Number.isFinite(openMinute)) return null;
    return openHour * 60 + openMinute;
};

const applyOpeningTimeToDate = (date, schedule) => {
    const openingMinutes = getOpeningMinutesByDate(date, schedule);
    if (!Number.isFinite(openingMinutes)) return null;
    const hours = Math.floor(openingMinutes / 60);
    const minutes = openingMinutes % 60;
    return setTimePortion(date, hours, minutes);
};

const resolveDailyOpeningStart = (referenceDate, schedule) => {
    const base = parseDateLike(referenceDate) || new Date();
    const dayOnly = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 0, 0, 0, 0);

    for (let attempt = 0; attempt < 14; attempt += 1) {
        const candidate = new Date(dayOnly.getFullYear(), dayOnly.getMonth(), dayOnly.getDate() + attempt);
        const withOpening = applyOpeningTimeToDate(candidate, schedule);
        if (withOpening) return withOpening;
    }

    return null;
};

const resolveDailyMinimumStartFromNow = ({ stepMinutes, locationSchedule }) => {
    const nowRounded = roundedDateWithInterval(new Date(), stepMinutes);
    const dayOnly = new Date(nowRounded.getFullYear(), nowRounded.getMonth(), nowRounded.getDate(), 0, 0, 0, 0);

    for (let attempt = 0; attempt < 14; attempt += 1) {
        const candidate = new Date(dayOnly.getFullYear(), dayOnly.getMonth(), dayOnly.getDate() + attempt);
        const withOpening = applyOpeningTimeToDate(candidate, locationSchedule);
        if (!withOpening) continue;
        if (withOpening.getTime() >= nowRounded.getTime()) return withOpening;
    }

    return nowRounded;
};

export {
    MS_IN_MINUTE,
    MS_IN_HOUR,
    DAILY_MINIMUM_RENTAL_MS,
    pad2,
    cloneDate,
    parseDateLike,
    roundedDateWithInterval,
    calculateDayExtensionHours,
    formatDate,
    formatTime,
    formatDateTime,
    formatPopupDateTime,
    formatPopupDuration,
    toInputDateValue,
    getMinuteOptions,
    alignMinuteToStep,
    setDatePortion,
    setTimePortion,
    isSameCalendarDate,
    getScheduleEntryForDate,
    getOpeningMinutesByDate,
    applyOpeningTimeToDate,
    resolveDailyOpeningStart,
    resolveDailyMinimumStartFromNow,
};
